import { useEffect, useRef } from 'react';

import { retintEventBackground, useResolvedTheme } from '../theme';

interface Props {
  anchorRect: DOMRect;
  currentHex: string | null;
  onPick: (hex: string) => void;
  onClose: () => void;
}

const HUE_COUNT = 12;
const HUES = Array.from({ length: HUE_COUNT }, (_, i) => (i * 360) / HUE_COUNT);

function hueToHex(hue: number): string {
  const h = ((hue % 360) + 360) % 360 / 60;
  const c = 1;
  const x = 1 - Math.abs((h % 2) - 1);
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 1) { r = c; g = x; }
  else if (h < 2) { r = x; g = c; }
  else if (h < 3) { g = c; b = x; }
  else if (h < 4) { g = x; b = c; }
  else if (h < 5) { r = x; b = c; }
  else { r = c; b = x; }
  const to = (v: number) => Math.round(v * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

export default function CalendarColorPicker({ anchorRect, currentHex, onPick, onClose }: Props) {
  const theme = useResolvedTheme();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    function onClick(e: MouseEvent) {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      onClose();
    }
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => document.addEventListener('mousedown', onClick), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
      clearTimeout(t);
    };
  }, [onClose]);

  // Anchor: open just to the right of the swatch.
  const left = Math.min(window.innerWidth - 200, anchorRect.right + 6);
  const top = Math.max(8, Math.min(window.innerHeight - 120, anchorRect.top - 4));

  const currentLower = currentHex?.toLowerCase() ?? null;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Calendar color"
      className="fixed z-40 rounded-md border border-[var(--border-strong)] bg-[var(--popover-bg)] p-2 shadow-[var(--shadow-md)]"
      style={{ left, top, width: 180 }}
    >
      <div className="mb-1 px-1 text-[0.7rem] uppercase tracking-wide text-[var(--text-faint)]">
        Color
      </div>
      <div className="grid grid-cols-6 gap-[6px]">
        {HUES.map((h) => {
          const hex = hueToHex(h);
          const tinted = retintEventBackground(hex, theme);
          const selected = currentLower === hex;
          return (
            <button
              key={hex}
              type="button"
              aria-label={`Pick ${hex}`}
              title={hex}
              onClick={() => onPick(hex)}
              className={
                'h-[22px] w-[22px] rounded-full border ' +
                (selected ? 'border-[var(--text)] ring-2 ring-[var(--accent)]' : 'border-transparent hover:border-[var(--text-faint)]')
              }
              style={{ background: tinted }}
            />
          );
        })}
      </div>
    </div>
  );
}
